import {
  CheckStatus,
  TwoLayerConditionSelectionStates,
} from '@/components/view/TwoLayerSearchConditionSelector'
import {
  Article,
  ContentfulInvestor,
  CVCInvestorInfo,
  Investor,
  InvestorSearchCondition,
  InvestorTabType,
  InvestorType,
  SearchInvestorCategories,
  SearchInvestorIndustries,
  VCInvestorInfo,
  WithId,
} from '../types'
import { getArticlesByInvestor, getContentfulInvestor } from './api/contentful'
import { convertArticle } from './article'
import { printError } from './log'

export const isVc = (i: Investor): i is VCInvestorInfo =>
  i.category === 'VC' || i.category === '海外VC'

export const isCvc = (i: Investor): i is CVCInvestorInfo => {
  return (
    i.category === '事業法人' ||
    i.category === '地方公共団体/大学/財団/その他基金' ||
    i.category === '金融機関' ||
    i.category === '年金基金' ||
    i.category === 'その他' ||
    i.category === 'SPC(特別目的会社)'
  )
}

export const investorTypeOf = (i: Investor): InvestorType => {
  return isVc(i) ? 'vc' : isCvc(i) ? 'cvc' : undefined
}

export const investorTabLabel: { [type in InvestorTabType]: string } = {
  'basic-info': '企業概要',
  'investment-info': '投資情報',
  companies: '投資先',
  'seed-funded': 'シード支援先',
  acquisitions: '買収',
  partnerships: '提携先',
  people: '人物',
  funds: 'ファンド',
  'startup-investments': 'スタートアップ投資',
  'lp-investments': 'LP投資',
  news: 'ニュース',
}

export const fetchArticlesByInvestor = async (investorId: string) => {
  let articles: Article[] = []
  try {
    const data = await getArticlesByInvestor(investorId)
    if (data) {
      articles = data.items.map((res) =>
        convertArticle({ ...res.fields, id: res.sys.id })
      )
    }
  } catch (e) {
    printError(e)
  }
  return articles
}

export const fetchContentfulInvestor = async (shortname: string) => {
  let investor: WithId<ContentfulInvestor> | null = null
  try {
    const data = await getContentfulInvestor(shortname)
    if (data) {
      investor = data
    }
  } catch (e) {
    printError(e)
  }
  return investor
}

export const toInvestorSearchQuery = (
  keywords: string,
  categories: SearchInvestorCategories,
  countries: string[],
  page: number,
  industries: SearchInvestorIndustries
): {
  keywords: string
  categories: string
  countries: string
  page: number
  industries: string
} => {
  const joinedCategories = categories
    .map((category) => {
      if (category.subCategories.length !== 0) {
        return category.name + ':' + category.subCategories.join('_')
      } else {
        return category.name
      }
    })
    .join(',')
  const joinedCountries = countries.join(',')
  return {
    keywords,
    categories: joinedCategories,
    countries: joinedCountries,
    page,
    industries: industries.join(','),
  }
}

export const parseInvestorSearchQuery = (
  keywords: string,
  categories: string,
  countries: string,
  page: string,
  industries: string
): {
  keywords: string
  categories: SearchInvestorCategories
  countries: string[]
  page: number
  industries: SearchInvestorIndustries
} => {
  const parsedCategories = categories
    .split(',')
    .filter((v) => v.length !== 0)
    .map((v) => {
      if (v.includes(':')) {
        const nameAndSubCategory = v.split(':')
        return {
          name: nameAndSubCategory[0],
          subCategories: nameAndSubCategory[1].split('_'),
        }
      } else {
        return { name: v, subCategories: [] }
      }
    })
  const parsedCountries = countries
    .split(',')
    .filter((v) => v.length !== 0)
    .filter((x): x is string => x !== undefined)
  const parsedPage = page !== '' ? Number(page) : 1

  const parsedIndustries = industries.length > 0 ? industries.split(',') : []

  return {
    keywords: keywords,
    categories: parsedCategories,
    countries: parsedCountries,
    page: parsedPage,
    industries: parsedIndustries,
  }
}

export const createTwoLayerConditionSelectionStates = (
  condition: InvestorSearchCondition,
  allCategories: SearchInvestorCategories
): TwoLayerConditionSelectionStates => {
  return allCategories.map((category) => {
    const initialSubCategories = category.subCategories.map((subCategory) => {
      const status =
        condition.categories
          .find((c) => c.name === category.name)
          ?.subCategories?.includes(subCategory) ?? false
      return { name: subCategory, status: status }
    })

    let checkStatus: CheckStatus
    if (category.subCategories.length === 0) {
      if (condition.categories.map((c) => c.name).includes(category.name)) {
        checkStatus = 'FULL_CHECKED'
      } else {
        checkStatus = 'NON_CHECKED'
      }
    } else {
      if (initialSubCategories.every((s) => s.status)) {
        checkStatus = 'FULL_CHECKED'
      } else if (initialSubCategories.some((s) => s.status)) {
        checkStatus = 'PARTIAL_CHECKED'
      } else {
        checkStatus = 'NON_CHECKED'
      }
    }

    return {
      name: category.name,
      children: initialSubCategories,
      status: checkStatus,
      isOpen: false,
    }
  })
}
